import { useCallback, useEffect } from "react";
import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { Flag } from "../../../../hooks/useFeatureFlags.types";
import { useToast } from "../../../../mantine/notifications/useToast";
import { getErrorText } from "../../../../utils/get-error-text";
import { DropzoneWithContent } from "../../../DropzoneWithContent/DropzoneWithContent";
import { useSiteSetupConsumersMutations } from "../../hooks/useSiteSetupConsumersMutations";
import type {
  Consumer,
  SiteSetupProcessForForm,
  TenantTableErrors
} from "../../SiteSetupAssistant.types";
import { formatFrontendDataToBackend } from "../../utils/formatFrontendDataToBackend";
import { TenantAddressAndParticipationSection } from "./TenantAddressAndParticipationSection";

export interface TenantFormItemsProps {
  tenantTableErrors: TenantTableErrors | null;
  formControl: Control<SiteSetupProcessForForm>;
  formErrors: FieldErrors<SiteSetupProcessForForm>;
  watch: UseFormWatch<SiteSetupProcessForForm>;
  onBlockNavigation: (blocked: boolean) => void;
  onDocumentUpload: (file: File) => void;
  onSetFormValue: UseFormSetValue<SiteSetupProcessForForm>;
}

function TenantFormItems({
  tenantTableErrors,
  formControl,
  formErrors,
  watch,
  onBlockNavigation,
  onSetFormValue,
  onDocumentUpload
}: TenantFormItemsProps) {
  const { showErrorToast } = useToast();
  const { postToGetConsumersMutation } = useSiteSetupConsumersMutations();
  const watchParticipation = watch("allTenantsParticipatingInMieterstrom");
  const watchSameAddress = watch("allTenantAddressesAreSameAsSiteAddress");

  const { featureFlags } = useFeatureFlags();
  const isLaikaPilotEnabled = featureFlags[Flag.LaikaPilot];

  const participationAndAddressSet =
    typeof watchParticipation !== "undefined" &&
    watchParticipation !== null &&
    typeof watchSameAddress !== "undefined" &&
    watchSameAddress !== null;
  const creationHasBeenTriggered =
    postToGetConsumersMutation.isError ||
    postToGetConsumersMutation.isSuccess ||
    postToGetConsumersMutation.isPending;

  const getConsumers = useCallback(
    async (data) => {
      try {
        const newConsumers: Array<Consumer> =
          await postToGetConsumersMutation.mutateAsync(
            formatFrontendDataToBackend(data)
          );
        onSetFormValue("consumers", newConsumers);
      } catch (e) {
        showErrorToast(getErrorText(e));
      }
    },
    [onSetFormValue, postToGetConsumersMutation, showErrorToast]
  );

  useEffect(() => {
    if (
      participationAndAddressSet &&
      (!formControl._formValues.consumers ||
        formControl._formValues.consumers.length === 0) &&
      !creationHasBeenTriggered
    ) {
      getConsumers(formControl._formValues as SiteSetupProcessForForm);
    }
  }, [
    creationHasBeenTriggered,
    formControl._formValues,
    getConsumers,
    participationAndAddressSet
  ]);

  const tenantSection = (
    <TenantAddressAndParticipationSection
      formControl={formControl}
      formErrors={formErrors}
      participationAndAddressSet={participationAndAddressSet}
      tenantTableErrors={tenantTableErrors}
      watch={watch}
      onBlockNavigation={onBlockNavigation}
    />
  );

  if (isLaikaPilotEnabled) {
    return (
      <>
        <DropzoneWithContent
          mb="sm"
          multiple={false}
          textContent="Ziehen Sie Ihre Datei in dieses Feld oder klicken Sie für die Dokumentenauswahl, um Ihre Mieterliste automatisch befüllen zu lassen."
          onDrop={(files) => onDocumentUpload(files[0])}
        />

        {tenantSection}
      </>
    );
  }

  return tenantSection;
}

export { TenantFormItems };
